import { useQuerystring } from "$routes/_lib/use-querystring";
import { serialisePatientInsuranceDetails } from "$routes/edit/patient/_lib/form-helpers";
import { gql } from "@apollo/client";
import {
  ContactPointOwnership,
  Gender,
  type PatientSyncPushInput,
  type QueryPullPatientArgs,
  type SyncablePatient,
  SyncablePatientMaritalStatus,
} from "elephant-web/gql";
import { useEffect, useMemo, useState } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Patient } from "../collections/patient";
import type { RxDBCollections } from "../provider";
import {
  SyncPatientPullDocument,
  SyncPatientPushDocument,
  type WholeSyncablePatientFragment,
} from "./__generated__/patient.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationOptions,
  useGqlReplication,
} from "./use-gql-replication";

/**
 * Replicates patients to and from the server.
 *
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 */
export const useReplicatePatient = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Patient>;
}) => {
  const query = useQuerystring();
  const v2RoutesMatch = useMatch("/dev-patient/:patientId/*");
  const editRoutesMatch = useMatch("edit/patient/:patientId/*");
  const [isPullingPatient, setIsPullingPatient] = useState(false);
  const patientId =
    v2RoutesMatch?.params.patientId ??
    editRoutesMatch?.params.patientId ??
    query.patientUuid;
  /**
   * NOTE: we need to set the pulling patient state early here,
   * since we redirect to home if the patient is not found, and
   * there is a race condition between the patient not initially
   * being found in RxDB, and the isPullingPatient state being set
   * to true when the patient is being fetched from the server.
   */
  useEffect(() => {
    if (patientId) {
      setIsPullingPatient(true);
    }
  }, [patientId]);

  const push = useMemo(
    () => ({
      mutation: SyncPatientPushDocument,
      mapRxToGql: mapRxPatientToGqlPatientInput,
    }),
    [],
  );

  const pull = useMemo((): ReplicationOptions<
    Patient,
    WholeSyncablePatientFragment,
    PatientSyncPushInput
  >["pull"] => {
    return patientId
      ? {
          query: SyncPatientPullDocument,
          reSyncInterval: { seconds: 30 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullPatientArgs,
            SyncablePatient
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: {
                limit: batchSize,
                filters: { id: patientId },
                checkpoint,
              },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
          onReplicationComplete: () => {
            setIsPullingPatient(false);
          },
        }
      : undefined;
  }, [patientId, db, collection]);

  const replicationState = useGqlReplication<
    Patient,
    SyncablePatient,
    PatientSyncPushInput
  >({
    db,
    collection,
    push,
    pull,
    mapGqlToRx: mapGqlSyncablePatientToRxPatient,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `patient-${patientId}`,
  });

  return {
    ...replicationState,
    isPulling: isPullingPatient,
  };
};

export const GENDER_MAP_RX_TO_GQL: Record<Patient["gender"], Gender> = {
  m: Gender.M,
  f: Gender.F,
  transgender: Gender.Transgender,
};
export const MARITAL_STATUS_MAP_RX_TO_GQL: Record<
  Patient["maritalStatus"],
  SyncablePatientMaritalStatus
> = {
  divorced: SyncablePatientMaritalStatus.Divorced,
  married: SyncablePatientMaritalStatus.Married,
  single: SyncablePatientMaritalStatus.Single,
  widowed: SyncablePatientMaritalStatus.Widowed,
  unknown: SyncablePatientMaritalStatus.Unknown,
};
/**
 * Warning: If you're using this outside of the RxDB module, something is amiss.
 * Other areas of the app should hold on concern for how RxDB data maps to GQL,
 * and should only be concerned with the GQL data.
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 */

export const mapRxPatientToGqlPatientInput = (
  patient: WithDeleted<Patient>,
): PatientSyncPushInput => ({
  address: patient.address,
  consent: patient.consent,
  dob: patient.dob,
  education: patient.education,
  facilityId: patient.facilityId,
  gender: GENDER_MAP_RX_TO_GQL[patient.gender],
  id: patient.id,
  insurance: patient.insurance
    ? {
        insuranceType: patient.insurance.type,
        insuranceNumber: patient.insurance.number,
        insuranceDetails: patient.insurance.details,
      }
    : undefined,
  newToFacility: patient.newToFacility,
  maritalStatus: MARITAL_STATUS_MAP_RX_TO_GQL[patient.maritalStatus],
  meta: {
    ...patient.meta,
    deleted: patient._deleted,
  },
  nationalId: patient.nationalId
    ? {
        owner: patient.nationalId.owner,
        value: patient.nationalId.value,
      }
    : undefined,
  name: patient.name,
  nationality: patient.nationality,
  nextOfKin: patient.nextOfKin,
  organisationId: patient.organisationId,
  phone: patient.phone
    ? {
        number: patient.phone.number,
        ownership: patient.phone.ownership ?? ContactPointOwnership.Personal,
      }
    : undefined,
  registrationFee: patient.registrationFee,
  guardians: patient.guardians?.map((g) => ({
    id: g.id,
    consents: {
      healthcareComms: {
        value: g.consents.healthcareComms?.value ?? false,
        providedAt:
          g.consents.healthcareComms?.providedAt ?? new Date().toISOString(),
      },
      marketingComms: {
        value: g.consents.marketingComms?.value ?? false,
        providedAt:
          g.consents.marketingComms?.providedAt ?? new Date().toISOString(),
      },
    },
    meta: g.meta,
    fullName: g.fullName,
    phone: g.phone,
    patientId: g.patientId,
    relationship: g.relationship,
  })),
});
/*
 * Warning: If you're using this outside of the RxDB module, something is amiss.
 * Other areas of the app should hold on concern for how RxDB data maps to GQL,
 * and should only be concerned with the GQL data.
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 *
 * It's more code writing out each field individually, but it's more performant
 * than spreads and stops us from saving fields like __typename onto the RxDB
 * document (RxDB never performs schema validation on what you put in it) which
 * makes conflicts more likely.
 */

export const mapGqlSyncablePatientToRxPatient = (
  patient: WholeSyncablePatientFragment,
): WithDeleted<Patient> => ({
  address: {
    country: patient.address.country,
    line1: patient.address.line1,
    line2: patient.address.line2,
    line3: patient.address.line3 ?? null,
    line4: patient.address.line4,
  },
  consent: {
    elephantUseOfAnonData: patient.consent.elephantUseOfAnonData,
    productMarketing: patient.consent.productMarketing,
    territoryUseOfData: patient.consent.territoryUseOfData,
    accessToHealthRecords: patient.consent.accessToHealthRecords,
    healthcareComms: patient.consent.healthcareComms,
  },
  dob: patient.dob,
  education: patient.education
    ? {
        isStudent: patient.education.isStudent,
        school: patient.education.school ?? null,
        nemisNumber: patient.education.nemisNumber ?? null,
      }
    : null,
  email: null,
  facilityId: patient.facilityId,
  gender: patient.gender,
  id: patient.id,
  insurance: patient.insurance
    ? {
        type: patient.insurance.insuranceType,
        details: patient.insurance.insuranceDetails
          ? serialisePatientInsuranceDetails(patient.insurance.insuranceDetails)
          : null,
        number: patient.insurance.insuranceNumber ?? null,
      }
    : null,
  maritalStatus: patient.maritalStatus,
  meta: {
    createdAt: patient.meta.createdAt,
    createdByUserId: patient.meta.createdByUserId,
    updatedAt: patient.meta.updatedAt,
    updatedByUserId: patient.meta.updatedByUserId,
  },
  name: {
    first: patient.name.first,
    middle: patient.name.middle ?? null,
    last: patient.name.last,
  },
  nationalId: patient.nationalId
    ? {
        owner: patient.nationalId.owner,
        value: patient.nationalId.value,
      }
    : null,
  nationality: patient.nationality,
  newToFacility: patient.newToFacility,
  nextOfKin: patient.nextOfKin
    ? {
        address: patient.nextOfKin.address
          ? {
              country: patient.nextOfKin.address.country ?? null,
              line1: patient.nextOfKin.address.line1 ?? null,
              line2: patient.nextOfKin.address.line2 ?? null,
              line3: patient.nextOfKin.address.line3 ?? null,
              line4: patient.nextOfKin.address.line4 ?? null,
            }
          : null,
        fullName: patient.nextOfKin.fullName,
        phoneNumber: patient.nextOfKin.phoneNumber ?? null,
        relationship: patient.nextOfKin.relationship ?? null,
      }
    : null,
  organisationId: patient.organisationId,
  phone: patient.phone
    ? {
        number: patient.phone.number,
        ownership: patient.phone.ownership,
      }
    : null,
  registrationFee: patient.registrationFee
    ? {
        paidOn: patient.registrationFee.paidOn,
        receiptNumber: patient.registrationFee.receiptNumber ?? null,
      }
    : null,
  _deleted: patient.meta.deleted,
  guardians:
    patient.guardians?.map((g) => ({
      id: g.id,
      consents: g.consents,
      meta: g.meta,
      fullName: g.fullName ?? null,
      relationship: g.relationship ?? null,
      phone: g.phone ?? null,
      patientId: g.patientId ?? null,
    })) ?? null,
});

gql`
  fragment WholeSyncablePatient on SyncablePatient {
    address {
      country
      line1
      line2
      line3
      line4
    }
    consent {
      elephantUseOfAnonData
      productMarketing
      territoryUseOfData
      accessToHealthRecords
      healthcareComms
    }
    dob
    education {
      isStudent
      school
      nemisNumber
    }
    facilityId
    gender
    id
    insurance {
      insuranceType
      insuranceNumber
      insuranceDetails
    }
    maritalStatus
    meta {
      createdAt
      createdByUserId
      deleted
      updatedAt
      updatedByUserId
    }
    name {
      first
      middle
      last
    }
    nationalId {
      owner
      value
    }
    nationality
    newToFacility
    nextOfKin {
      address {
        country
        line1
        line2
        line3
        line4
      }
      fullName
      phoneNumber
      relationship
    }
    organisationId
    phone {
      number
      ownership
    }
    registrationFee {
      paidOn
      receiptNumber
    }
    guardians {
      id
      patientId
      fullName
      phone {
        number
        ownership
      }
      relationship
      consents {
        healthcareComms {
          value
          providedAt
        }
        marketingComms {
          value
          providedAt
        }
      }
      meta {
        createdAt
        createdByUserId
        updatedAt
        updatedByUserId
        deleted
      }
    }
  }
`;

gql`
  mutation SyncPatientPush($input:PushPatientInput!) {
    pushPatient(input: $input) {
      rows {
        ...WholeSyncablePatient
      }
    }
  }
`;

gql`
  query SyncPatientPull($input: PullPatientInput!) {
    pullPatient(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePatient
      }
    }
  }
`;
